import { useCallback, useRef } from "react";

type RefMap<T> = Record<string, React.MutableRefObject<T | null>>;

export function usePreservedRef<T extends object>() {
  const refs = useRef<RefMap<T>>({});
  const lastValues = useRef<Record<string, T | null>>({});
  const getRef = useCallback((key: string): React.RefObject<T> => {
    if (!refs.current[key] || refs.current[key].current === null) {
      refs.current[key] = { current: lastValues.current[key] ?? null };
    }
    return refs.current[key];
  }, []);

  const setCustomPropertyOnRef = useCallback(
    (
      key: string,
      additionalPropertyToChange: {
        propertyName: string;
        value: string | number | boolean | object | null;
      }
    ): void => {
      const { propertyName, value } = additionalPropertyToChange;
      let currentValueForKey = refs.current[key]?.current || null;
      if (!currentValueForKey && lastValues?.current[key]) {
        currentValueForKey = lastValues.current[key];
      }
      if (currentValueForKey !== null) {
        refs.current[key] = {
          current: {
            ...currentValueForKey,
            [propertyName]: value
          }
        };
        lastValues.current[key] = {
          ...currentValueForKey,
          [propertyName]: value
        };
      }
    },
    []
  );

  const getValue = useCallback((key: string): T | null => {
    return refs.current[key]?.current ?? lastValues.current[key] ?? null;
  }, []);

  const register = useCallback(
    (
      key: string,
      value: T,
      additionalPropertyToChange?: {
        propertyName: string;
        value: string | number | boolean | object | null;
      }
    ) => {
      const currentValueForKey =
        refs.current[key]?.current || lastValues.current[key] || null;
      const currentValueWithAdditionalProperty: T = {
        ...currentValueForKey,
        ...(additionalPropertyToChange && {
          [additionalPropertyToChange.propertyName]:
            additionalPropertyToChange.value
        })
      } as T;

      lastValues.current[key] = {
        ...currentValueWithAdditionalProperty,
        ...value
      };

      if (!refs.current[key]) {
        refs.current[key] = {
          current: {
            ...currentValueWithAdditionalProperty,
            ...value
          }
        };
      } else {
        refs.current[key].current = {
          ...currentValueWithAdditionalProperty,
          ...value
        };
      }
    },
    []
  );
  const unregister = useCallback(
    (
      key: string,
      additionalPropertyToChange?: {
        propertyName: string;
        value: string | number | boolean | null;
      }
    ) => {
      if (refs.current[key]?.current) {
        const newValueToCache = {
          ...(lastValues.current[key] as T),
          ...(additionalPropertyToChange
            ? {
                [additionalPropertyToChange.propertyName]:
                  additionalPropertyToChange.value
              }
            : {})
        };
        lastValues.current[key] = newValueToCache;
      }
      if (refs.current[key]) {
        refs.current[key].current = null;
      }
    },
    []
  );

  const clearAllRefs = useCallback(() => {
    refs.current = {};
    lastValues.current = {};
  }, []);

  return {
    getRef,
    getValue,
    register,
    unregister,
    clearAllRefs,
    setCustomPropertyOnRef
  };
}
